import {
    _forOwn, _forEach, _isEmpty, _some, _every, _includes,
} from '@samclick-lodash';
import i18n from '@/i18next';
import AuthorizationStore from '@/state/modules/authorization';
import * as appStorageUtil from '@/utils/localstorage-util';

export function multipleReplace(str: string, obj: object) {
    let retStr = str;

    _forOwn(obj, (value, key) => {
        retStr = retStr.replace(new RegExp(key, 'g'), value);
    });

    return retStr;
}

export function serializeToQueryParams(payload: object, emitEmpty: boolean = false) {
    let queryParams = '';
    _forOwn(payload, (val, key) => {
        if (val instanceof Array) {
            _forEach(val, (subVal) => {
                if (subVal) {
                    queryParams += `${key}=${subVal}&`;
                }
            });
        } else if (val) {
            queryParams += `${key}=${val}&`;
        }
    });

    return queryParams.slice(0, -1);
}

export function toMoney(money: number | string, decimal: number = 2, addComma: boolean = true) {
    let currency = 0;
    if (typeof money === 'string') {
        currency = Number(money);
    } else {
        currency = money;
    }

    let stringMoney = currency.toFixed(6).slice(0, -4);

    if (addComma) {
        stringMoney = stringMoney.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }

    return stringMoney;
}

export function roundMoneyAmount(money: number | string = 0.00, useAbsolute: boolean = false) {
    const parsedMoney = Number(money) || 0.00;
    const normalizedMoney = useAbsolute ? Math.abs(parsedMoney) : parsedMoney;
    return Math.round((normalizedMoney + Number.EPSILON) * 100) / 100;
}

function validateAll(all: (string | number)[] = []): boolean {
    if (_isEmpty(all)) {
        return true;
    }

    return _every(all, value => _includes(AuthorizationStore.getCurrentUserRolesInState, value));
}

function validateAny(any: (string | number)[]): boolean {
    if (_isEmpty(any)) {
        return true;
    }

    return _some(any, value => _includes(AuthorizationStore.getCurrentUserRolesInState, value));
}

export function validateRoles(all: (string | number)[] = [], any: (string | number)[] = []): boolean {
    return validateAll(all) && validateAny(any);
}

export function formatBytes(bytes: string | number, decimals: number = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function sortString(a: string = '', b: string = '') {
    const m = a.toLowerCase();
    const p = b.toLowerCase();

    if (!m) return -1;
    if (!p) return 1;

    return m.localeCompare(p);
}

export function extractSRC(scriptTag: string) {
    let src = '';
    const regex = /<script.*?src="(.*?)"/;
    const result = regex.exec(scriptTag);
    if (result) {
        const [input, resource] = result;
        src = resource;
    }
    return src;
}

/**
 * Get the user IP throught the webkitRTCPeerConnection
 * @param onNewIP {Function} listener function to expose the IP locally
 * @return undefined
 */
export function getUserIP(onNewIP: any) { //  onNewIp - your listener function for new IPs
    // compatibility for firefox and chrome
    const myPeerConnection = window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection;
    const pc = new myPeerConnection({
        iceServers: [],
    });
    const noop = function () {};
    const localIPs = {};
    const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/g;
    let key;

    function iterateIP(ip) {
        if (!localIPs[ip]) onNewIP(ip);
        localIPs[ip] = true;
    }

    // create a bogus data channel
    pc.createDataChannel('');

    // create offer and set local description
    pc.createOffer().then((sdp) => {
        sdp.sdp.split('\n').forEach((line) => {
            if (line.indexOf('candidate') < 0) return;
            line.match(ipRegex).forEach(iterateIP);
        });

        pc.setLocalDescription(sdp, noop, noop);
    }).catch((reason) => {
    // An error occurred, so handle the failure to connect
    });

    // listen for candidate events
    pc.onicecandidate = function (ice) {
        if (!ice || !ice.candidate || !ice.candidate.candidate || !ice.candidate.candidate.match(ipRegex)) return;
        ice.candidate.candidate.match(ipRegex).forEach(iterateIP);
    };
}

export function emailRegex() {
    const regex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/g;
    return regex;
}

export function getPercentageRate(num1: number, num2: number) {
    if (Number.isNaN(num1) || Number.isNaN(num2) || num1 === undefined || num1 === undefined || num2 === 0) {
        return 0;
    }

    return ((Number(num1) / Number(num2)) * 100).toFixed(2);
}

export function getBrowserLocale(options = {}) {
    const defaultOptions = { countryCodeOnly: false };
    const opt = { ...defaultOptions, ...options };
    const navigatorLocale = navigator.languages !== undefined
        ? navigator.languages[0]
        : navigator.language;
    if (!navigatorLocale) {
        return undefined;
    }
    const trimmedLocale = opt.countryCodeOnly
        ? navigatorLocale.trim().split(/-|_/)[0]
        : navigatorLocale.trim();
    return trimmedLocale;
}

export function cloneProperty(objToClone: any) {
    return JSON.parse(JSON.stringify(objToClone));
}

export function translateProperty(obj: any, property: string) {
    if (_isEmpty(obj) || _isEmpty(obj[property])) {
        return obj;
    }
    const objectToModify = cloneProperty(obj);

    objectToModify[property] = i18n.t(objectToModify[property]);

    return objectToModify;
}

export function isBoa() {
    return (process.env.VUE_APP_IS_BOA === 'true');
}

export function getLanguageLookup() {
    let language = '';
    const currUserLang = window.localStorage.getItem(appStorageUtil.CURRENT_USER_LANG);
    if (isBoa()) {
        if (currUserLang === 'en' || i18n.i18next.language === 'en') {
            language = 'lookups.lang_english';
        } else {
            language = 'lookups.lang_portuguese';
        }
    } else if (currUserLang === 'en' || i18n.i18next.language === 'en') {
        language = 'lookups.lang_english';
    } else {
        language = 'lookups.lang_japanese';
    }

    return language;
}

export function getPreferredLanguages(languages = [{ value: '', label: '' }]) {
    let preferredLanguages = [{ value: '', label: '' }];
    if (isBoa()) {
        preferredLanguages = languages.filter(item => item.value !== 'jp');
    } else {
        preferredLanguages = languages.filter(item => item.value !== 'br');
    }
    return preferredLanguages;
}
